class SpBrandHero extends HTMLElement {
  constructor() {
    super();
    this.slides = this.querySelectorAll('[data-sp-hero-slide]');
    this.defaultIntervalMs = Number(this.dataset.interval) || 6000;
    this.timer = null;
    this.index = 0;
    this.cta = this.querySelector('[data-sp-hero-cta]');
    this.ctaPrice = this.querySelector('[data-sp-hero-cta-price]');
    this.ctaLabel = this.querySelector('[data-sp-hero-cta-label]');
    this.onVideoEnded = null;
    this.dots = this.querySelectorAll('[data-sp-hero-dot]');
    this.pauseTimers = new Map();
    this.prefersReducedMotion = false;
    this.transitionMs = 950;
  }

  connectedCallback() {
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.transitionMs = this.prefersReducedMotion ? 0 : 950;
    this.preloadSlideVideos();

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.spHeroDot);
        if (!Number.isNaN(index)) this.goTo(index);
      });
    });

    this.show(0);
    if (this.slides.length >= 2) {
      this.scheduleAdvance();
      this.bindCarouselControls();
    }
  }

  bindCarouselControls() {
    const target = this.querySelector('.sp-brand-hero__media') || this;
    let touchStartX = 0;
    let touchStartY = 0;
    let didSwipe = false;

    target.addEventListener('click', (event) => {
      if (didSwipe) {
        didSwipe = false;
        return;
      }

      if (event.target.closest('a, button, [data-sp-hero-dot], sp-hero-nav, .sp-brand-hero__cta')) {
        return;
      }

      const rect = target.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const ratio = x / rect.width;

      if (ratio < 0.38) this.prev();
      else if (ratio > 0.62) this.next();
    });

    target.addEventListener(
      'touchstart',
      (event) => {
        didSwipe = false;
        touchStartX = event.changedTouches[0].screenX;
        touchStartY = event.changedTouches[0].screenY;
      },
      { passive: true }
    );

    target.addEventListener(
      'touchend',
      (event) => {
        const touchEndX = event.changedTouches[0].screenX;
        const touchEndY = event.changedTouches[0].screenY;
        const deltaX = touchStartX - touchEndX;
        const deltaY = touchStartY - touchEndY;

        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;

        didSwipe = true;
        if (deltaX > 0) this.next();
        else this.prev();
      },
      { passive: true }
    );
  }

  disconnectedCallback() {
    this.clearAdvance();
    this.clearPauseTimers();
  }

  clearPauseTimers() {
    this.pauseTimers.forEach((timer) => window.clearTimeout(timer));
    this.pauseTimers.clear();
  }

  preloadSlideVideos() {
    this.slides.forEach((slide) => {
      const video = slide.querySelector('.sp-brand-hero__video');
      if (!video) return;

      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      if (video.readyState < 1) video.load();
    });
  }

  clearAdvance() {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    if (this.onVideoEnded) {
      const { video, handler } = this.onVideoEnded;
      video.removeEventListener('ended', handler);
      this.onVideoEnded = null;
    }
  }

  scheduleAdvance() {
    this.clearAdvance();
    if (this.slides.length < 2) return;

    const slide = this.slides[this.index];
    const video = slide?.querySelector('.sp-brand-hero__video');

    if (video) {
      const goNext = () => {
        if (slide.classList.contains('is-active')) this.next();
      };

      const armFallback = () => {
        const durationMs = video.duration && !Number.isNaN(video.duration)
          ? Math.ceil(video.duration * 1000) + 300
          : this.defaultIntervalMs;

        this.timer = window.setTimeout(goNext, durationMs);
      };

      const onEnded = () => {
        if (this.timer) window.clearTimeout(this.timer);
        goNext();
      };

      video.addEventListener('ended', onEnded);
      this.onVideoEnded = {
        video,
        handler: onEnded,
      };

      if (video.readyState >= 1) armFallback();
      else video.addEventListener('loadedmetadata', armFallback, { once: true });
      return;
    }

    this.timer = window.setTimeout(() => this.next(), this.defaultIntervalMs);
  }

  next() {
    const nextIndex = (this.index + 1) % this.slides.length;
    this.goTo(nextIndex);
  }

  prev() {
    const prevIndex = (this.index - 1 + this.slides.length) % this.slides.length;
    this.goTo(prevIndex);
  }

  goTo(activeIndex) {
    if (activeIndex === this.index && this.slides[activeIndex]?.classList.contains('is-active')) {
      return;
    }

    this.clearAdvance();
    this.show(activeIndex);
    if (this.slides.length >= 2) this.scheduleAdvance();
  }

  show(activeIndex) {
    const incoming = this.slides[activeIndex];
    if (incoming) this.prepareIncomingSlide(incoming);

    this.slides.forEach((slide, i) => {
      const isActive = i === activeIndex;
      slide.classList.toggle('is-active', isActive);
      if (!isActive) this.deactivateSlideMedia(slide);
    });

    if (incoming) this.activateSlideMedia(incoming);

    this.syncCta(activeIndex);
    this.syncDots(activeIndex);
    this.syncSiteHeader(activeIndex);
    this.index = activeIndex;
  }

  prepareIncomingSlide(slide) {
    const video = slide.querySelector('.sp-brand-hero__video');
    if (!video) return;

    video.currentTime = 0;
    const startPlayback = () => {
      const playPromise = video.play();
      if (playPromise) playPromise.catch(() => {});
    };

    if (video.readyState >= 2) startPlayback();
    else video.addEventListener('canplay', startPlayback, { once: true });
  }

  activateSlideMedia(slide) {
    const video = slide.querySelector('.sp-brand-hero__video');
    if (!video || !video.paused) return;

    const playPromise = video.play();
    if (playPromise) playPromise.catch(() => {});
  }

  deactivateSlideMedia(slide) {
    const video = slide.querySelector('.sp-brand-hero__video');
    if (!video) return;

    const existingTimer = this.pauseTimers.get(video);
    if (existingTimer) window.clearTimeout(existingTimer);

    const pauseLater = () => {
      if (!slide.classList.contains('is-active')) video.pause();
      this.pauseTimers.delete(video);
    };

    if (this.transitionMs > 0) {
      this.pauseTimers.set(video, window.setTimeout(pauseLater, this.transitionMs));
    } else {
      pauseLater();
    }
  }

  syncSlideMedia(slide, isActive) {
    if (isActive) this.activateSlideMedia(slide);
    else this.deactivateSlideMedia(slide);
  }

  syncSiteHeader(activeIndex) {
    const header = document.querySelector('[data-sp-site-header]');
    if (!header || document.body.classList.contains('sp-inner-page')) return;

    header.classList.remove('sp-site-header--hero-slide-light', 'sp-site-header--hero-slide-navy');
    const whiteTextSlides = [0, 2];
    if (whiteTextSlides.includes(activeIndex)) {
      header.classList.add('sp-site-header--hero-slide-light');
    } else {
      header.classList.add('sp-site-header--hero-slide-navy');
    }
  }

  syncDots(activeIndex) {
    if (!this.dots.length) return;

    this.dots.forEach((dot, i) => {
      const isActive = i === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  syncCta(activeIndex) {
    if (!this.cta) return;

    const slide = this.slides[activeIndex];
    if (!slide) return;

    const url = slide.dataset.ctaUrl;
    const price = slide.dataset.ctaPrice;
    const label = slide.dataset.ctaLabel;

    if (url) this.cta.href = url;
    if (this.ctaPrice && price) this.ctaPrice.textContent = price;
    if (this.ctaLabel && label) this.ctaLabel.textContent = label;
  }
}

if (!customElements.get('sp-brand-hero')) {
  customElements.define('sp-brand-hero', SpBrandHero);
}

class SpHeroNav extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-sp-nav-toggle]');
    this.panel = this.querySelector('[data-sp-nav-panel]');
    this.onScroll = () => this.syncStuck();

    this.syncStuck();
    window.addEventListener('scroll', this.onScroll, { passive: true });

    if (!this.toggle || !this.panel) return;

    this.toggle.addEventListener('click', () => {
      const open = this.panel.classList.toggle('is-open');
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  syncStuck() {
    if (document.body.classList.contains('sp-inner-page')) {
      this.classList.add('is-stuck');
      return;
    }
    const stuck = window.scrollY > 48;
    this.classList.toggle('is-stuck', stuck);
  }
}

if (!customElements.get('sp-hero-nav')) {
  customElements.define('sp-hero-nav', SpHeroNav);
}

class SpTaglineRotator extends HTMLElement {
  connectedCallback() {
    this.textEl = this.querySelector('[data-sp-tagline-text]');
    if (!this.textEl) return;

    const raw = this.dataset.phrases || '';
    this.phrases = raw.split('|').map((p) => p.trim()).filter(Boolean);
    if (this.phrases.length === 0) return;

    this.index = 0;
    this.holdMs = Math.min(Number(this.dataset.interval) || 1500, 1500);
    this.typeSpeed = 158;
    this.fadeOutMs = 560;
    this.isAnimating = false;
    this.rotateTimer = null;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.start().then(() => {
      if (this.phrases.length >= 2) this.scheduleNext();
    });
  }

  disconnectedCallback() {
    if (this.rotateTimer) window.clearTimeout(this.rotateTimer);
  }

  scheduleNext() {
    if (this.rotateTimer) window.clearTimeout(this.rotateTimer);
    this.rotateTimer = window.setTimeout(() => this.rotate(), this.holdMs);
  }

  wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  getSouthRange(phrase) {
    const match = phrase.match(/(south!?)/i);
    if (!match) return null;

    const start = phrase.search(/south!?/i);
    return { start, end: start + match[0].length, word: match[0] };
  }

  getSpecialRanges(phrase, phraseIndex) {
    const ranges = [];

    if (phraseIndex === 0) {
      ['Caribe', 'Patagonia'].forEach((word) => {
        const pattern = new RegExp(`\\b${word}\\b`, 'i');
        const match = phrase.match(pattern);
        if (!match) return;

        const start = phrase.search(pattern);
        ranges.push({ start, end: start + word.length, className: 'sp-tagline__cap' });
      });
    }

    const south = this.getSouthRange(phrase);
    if (south) {
      ranges.push({ start: south.start, end: south.end, className: 'sp-tagline__south' });
    }

    return ranges.sort((a, b) => a.start - b.start);
  }

  buildTypingHtml(phrase, phraseIndex, length) {
    const ranges = this.getSpecialRanges(phrase, phraseIndex);
    let html = '';

    for (let i = 0; i < length; i += 1) {
      const range = ranges.find((entry) => i >= entry.start && i < entry.end);

      if (range && i === range.start) {
        html += `<span class="${range.className}">`;
      }

      if (range && range.className === 'sp-tagline__south') {
        const letterIndex = i - range.start;
        html += `<span class="sp-tagline__south-letter" style="${this.southLetterStyle(letterIndex)}">${this.escapeHtml(phrase[i])}</span>`;
      } else {
        html += this.escapeHtml(phrase[i]);
      }

      const isLastTyped = i === length - 1;
      const isRangeEnd = range && i === range.end - 1;

      if (range && (isRangeEnd || (isLastTyped && i < range.end - 1))) {
        html += '</span>';
      }
    }

    return html;
  }

  getTypeDelay() {
    const jitter = Math.floor(Math.random() * 50) - 18;
    return this.typeSpeed + jitter;
  }

  southLetterStyle(index) {
    const delays = [0, 0.11, 0.2, 0.36, 0.44, 0.58];
    const rotations = [-18, 12, -10, 16, -14, 9];
    const offsets = [-0.18, 0.22, -0.12, 0.2, -0.16, 0.1];
    const delay = (delays[index] ?? index * 0.11).toFixed(2);
    const rotate = rotations[index % rotations.length];
    const offset = offsets[index % offsets.length];

    return `--sp-south-delay:${delay}s;--sp-south-rotate:${rotate}deg;--sp-south-x:${offset}em`;
  }

  formatSouthWord(word) {
    const letters = [...word];
    const markup = letters
      .map((letter, index) => {
        return `<span class="sp-tagline__south-letter" style="${this.southLetterStyle(index)}">${this.escapeHtml(letter)}</span>`;
      })
      .join('');

    return `<span class="sp-tagline__south">${markup}</span>`;
  }

  formatPhrase(phrase, index) {
    const southRange = this.getSouthRange(phrase);
    if (southRange) {
      const before = this.escapeHtml(phrase.slice(0, southRange.start));
      const after = this.escapeHtml(phrase.slice(southRange.end));
      return `${before}${this.formatSouthWord(southRange.word)}${after}`;
    }

    return this.buildTypingHtml(phrase, index, phrase.length);
  }

  triggerSouthImpact() {
    const south = this.textEl.querySelector('.sp-tagline__south');
    if (!south) return;

    south.classList.remove('is-impacting');
    void south.offsetWidth;
    south.classList.add('is-impacting');
  }

  showFormattedPhrase(index) {
    this.textEl.classList.remove('is-typing', 'is-leaving');
    this.textEl.style.opacity = '';
    this.textEl.innerHTML = this.formatPhrase(this.phrases[index], index);
    this.index = index;
    this.triggerSouthImpact();
  }

  async fadeOutPhrase() {
    this.textEl.classList.remove('is-typing', 'is-impacting');
    this.textEl.querySelector('.sp-tagline__south')?.classList.remove('is-impacting');
    this.textEl.classList.add('is-leaving');
    await this.wait(this.fadeOutMs);
    this.textEl.classList.remove('is-leaving');
    this.textEl.innerHTML = '';
  }

  async typePhrase(index) {
    const phrase = this.phrases[index];
    const southRange = this.getSouthRange(phrase);
    this.textEl.classList.add('is-typing');
    this.textEl.innerHTML = '';

    for (let i = 1; i <= phrase.length; i += 1) {
      this.textEl.innerHTML = this.buildTypingHtml(phrase, index, i);
      await this.wait(this.getTypeDelay());
    }

    this.index = index;

    if (southRange) {
      this.triggerSouthImpact();
    }
  }

  start() {
    this.showFormattedPhrase(0);
    return Promise.resolve();
  }

  async rotate() {
    if (!this.textEl || this.phrases.length < 2 || this.isAnimating) return;

    this.isAnimating = true;

    const nextIndex = (this.index + 1) % this.phrases.length;

    if (!this.prefersReducedMotion) {
      await this.fadeOutPhrase();
    }

    this.showFormattedPhrase(nextIndex);

    this.isAnimating = false;
    this.scheduleNext();
  }
}

if (!customElements.get('sp-tagline-rotator')) {
  customElements.define('sp-tagline-rotator', SpTaglineRotator);
}
